import { createContext, useContext, useEffect, useState, ReactNode } from 'react';
import { createApp, ClientApplication } from '@shopify/app-bridge';
import { getSessionToken } from '@shopify/app-bridge/utilities';

interface ShopifyContextType {
  app: ClientApplication | null;
  host: string | null;
  shop: string | null;
  isEmbedded: boolean;
  getAuthenticatedFetch: () => (input: RequestInfo, init?: RequestInit) => Promise<Response>;
  sessionToken: string | null;
  loading: boolean;
}

const ShopifyContext = createContext<ShopifyContextType | null>(null);

export function ShopifyProvider({ children, apiKey }: { children: ReactNode; apiKey: string }) {
  const [app, setApp] = useState<ClientApplication | null>(null);
  const [sessionToken, setSessionToken] = useState<string | null>(null);
  const [loading, setLoading] = useState(true);

  // Get URL params
  const urlParams = new URLSearchParams(window.location.search);
  const host = urlParams.get('host');
  const shop = urlParams.get('shop');
  const isEmbedded = !!host;

  useEffect(() => {
    if (!isEmbedded) {
      setLoading(false);
      return;
    }

    // Initialize App Bridge
    const appInstance = createApp({
      apiKey,
      host: host!,
      forceRedirect: true,
    });

    setApp(appInstance);

    // Get initial session token
    const fetchToken = async () => {
      try {
        const token = await getSessionToken(appInstance);
        setSessionToken(token);
      } catch (error) {
        console.error('Failed to get session token:', error);
      }
      setLoading(false);
    };

    fetchToken();

    // Refresh token periodically (they expire in 1 minute)
    const tokenRefreshInterval = setInterval(async () => {
      try {
        const token = await getSessionToken(appInstance);
        setSessionToken(token);
      } catch (error) {
        console.error('Failed to refresh session token:', error);
      }
    }, 50000); // Refresh every 50 seconds

    return () => clearInterval(tokenRefreshInterval);
  }, [apiKey, host, isEmbedded]);

  // Create an authenticated fetch function that includes the session token
  const getAuthenticatedFetch = () => {
    return async (input: RequestInfo, init?: RequestInit): Promise<Response> => {
      let currentToken = sessionToken;

      // Get fresh token if we have an app instance
      if (app) {
        try {
          currentToken = await getSessionToken(app);
          setSessionToken(currentToken);
        } catch (error) {
          console.error('Failed to get fresh token:', error);
        }
      }

      const headers = new Headers(init?.headers);

      if (currentToken) {
        headers.set('Authorization', `Bearer ${currentToken}`);
      }

      return fetch(input, {
        ...init,
        headers,
      });
    };
  };

  return (
    <ShopifyContext.Provider
      value={{
        app,
        host,
        shop,
        isEmbedded,
        getAuthenticatedFetch,
        sessionToken,
        loading,
      }}
    >
      {children}
    </ShopifyContext.Provider>
  );
}

export function useShopify() {
  const context = useContext(ShopifyContext);
  if (!context) {
    throw new Error('useShopify must be used within a ShopifyProvider');
  }
  return context;
}

// Helper to detect if we're in embedded mode
export function isEmbeddedApp(): boolean {
  const urlParams = new URLSearchParams(window.location.search);
  return !!urlParams.get('host');
}
